import { TFunction, useTranslation } from 'react-i18next';
import styles from './CreateLeaderboardPage.module.less';
import {
	Col,
	Row,
	Form,
	Button,
	Radio,
	Select,
	Space,
	RadioChangeEvent,
	DatePicker,
	Spin,
	Checkbox,
} from 'antd';
import FormInput from 'components/form/FormItem/FormInput';
import { useHistory, useParams } from 'react-router-dom';
import api, {
	LeaderboardScoreType,
	LeaderboardEntryWritePolicy,
	LeaderboardsSortOrder,
	UpsertLeaderboardRequest,
	LeaderboardV2Dto,
} from 'api';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { ReactNode, useEffect, useState } from 'react';
import OmniNotification from 'components/OmniNotification';
import { useSelector } from 'react-redux';
import { projectSelector } from '../../../redux/selectors/projectSelector';
import Breadcrumb, { IRoutes } from '../../../components/Breadcrumb/Breadcrumb';

type ParamsProps = {
	projectId: string;
};

type Props = {
	leaderboard?: LeaderboardV2Dto;
	loading?: boolean;
	children?: ReactNode;
};

const defaultValues: UpsertLeaderboardRequest = {
	title: '',
	apiName: '',
	sortOrder: LeaderboardsSortOrder.HIGHER_IS_BETTER,
	scoreType: LeaderboardScoreType.POINT,
	entryWritePolicy: LeaderboardEntryWritePolicy.CLIENT_AUTHORITATIVE,
	isPublic: false,
	enableSurpassedNotification: false,
};

var projectId = 0;

var originalApiName = '';
var isTriggeredBySubmit = false;

const getValidationSchema = (
	t: TFunction,
	productId: string,
	existingApiName: string | undefined,
) =>
	yup.object().shape({
		apiName: yup
			.string()
			.required(
				t('validation.required', {
					fieldName: t('createLeaderboardPage.apiName.label'),
				}),
			)
			.test(
				'valid-apiName-free',
				({ value }) =>
					t('createLeaderboardPage.apiName.alreadyTaken', { name: value }),
				async (value, context) => {
					if (isTriggeredBySubmit) {
						if (existingApiName) return true;
						return api.validateLeaderboardV2ApiName({
							apiName: value,
							productId,
						});
					} else if (value !== originalApiName) {
						originalApiName = value!;
						return api.validateLeaderboardV2ApiName({
							apiName: value,
							productId,
						});
					} else {
						return true;
					}
				},
			)
			.matches(
				/^(?!.*[?;\/:@=&]).*$/,
				t('common.unsafeCharacters', {
					fieldName: t('createLeaderboardPage.apiName.label'),
				}),
			)
			.matches(
				/^[\S]*$/,
				t('common.noSpaces', {
					fieldName: t('createLeaderboardPage.apiName.label'),
				}),
			)
			.max(
				50,
				t('validation.maxLength', {
					fieldName: t('createLeaderboardPage.apiName.label'),
					count: 50,
				}),
			),
		title: yup
			.string()
			.required(
				t('validation.required', {
					fieldName: t('createLeaderboardPage.title.label'),
				}),
			)
			.max(
				80,
				t('validation.maxLength', {
					fieldName: t('createLeaderboardPage.title.label'),
					count: 80,
				}),
			),
	});

export default function CreateLeaderboardPage(props: Props | undefined) {
	const { t } = useTranslation();
	const history = useHistory();
	const projectInfo = useSelector(projectSelector);
	const validationSchema: any = getValidationSchema(
		t,
		projectInfo?.productId as string,
		props?.leaderboard?.apiName,
	);
	const [confirmLoading, setConfirmLoading] = useState(false);
	const [routes, setRoutes] = useState<IRoutes[]>([]);

	projectId = Number.parseInt(useParams<ParamsProps>().projectId);

	const pageForm = useForm<UpsertLeaderboardRequest>({
		mode: 'onChange',
		resolver: yupResolver(validationSchema),
		defaultValues,
	});

	const watchIsPublic = pageForm.watch('isPublic');

	const watchEntireForm = pageForm.watch();

	useEffect(() => {
		isTriggeredBySubmit = false;
	}, [watchEntireForm]);

	useEffect(() => {
		if (props?.leaderboard) {
			pageForm.reset(props?.leaderboard);
		}
	}, [props?.leaderboard]);

	useEffect(() => {
		setRoutes([
			{
				path: `project/${projectInfo?.id}`,
				breadcrumbName: projectInfo?.name as string,
			},
			{
				path: `game-services`,
				breadcrumbName: t('gameServicesPage.header'),
			},
			{
				path: 'leaderboardsV2',
				breadcrumbName: t('leaderboardsPage.header'),
			},
			{
				path: '',
				breadcrumbName: props?.leaderboard
					? t('leaderboardsPage.edit')
					: t('createLeaderboardPage.header'),
			},
		]);
	}, [projectInfo?.id, projectInfo?.name, props?.leaderboard]);

	const navigateToLeaderboardsIndex = () => {
		history.push(`/project/${projectId}/game-services/leaderboardsV2`);
	};

	useEffect(() => {
		if (!watchIsPublic) {
			pageForm.setValue('enableSurpassedNotification', false);
		}
	}, [watchIsPublic]);

	const onSubmit = async (values) => {
		values.productId = projectInfo?.productId;

		setConfirmLoading(true);

		api
			.upsertLeaderboard(projectInfo?.productId!, {
				...values,
			})
			.then(() => {
				if (props?.leaderboard?.apiName) {
					OmniNotification.success({
						message: t('createLeaderboardPage.notification.update.message'),
						description: t(
							'createLeaderboardPage.notification.update.description',
							{
								title: values.title,
							},
						),
					});
				} else {
					OmniNotification.success({
						message: t('createLeaderboardPage.notification.create.message'),
						description: t(
							'createLeaderboardPage.notification.create.description',
							{
								title: values.title,
							},
						),
					});
				}
				navigateToLeaderboardsIndex();
			})
			.catch((e) => {
				OmniNotification.error({
					message: t('notification.error.title'),
					description: t('api.defaultError'),
				});
			})
			.finally(() => {
				setConfirmLoading(false);
			});
	};

	return (
		<Spin spinning={props?.loading || false}>
			<div className={styles.pageContainer}>
				<Breadcrumb routes={routes} />
				<div className="page-head">
					<div>
						<h2 className="h2" style={{ wordBreak: 'break-all' }}>
							{props?.leaderboard
								? t('createLeaderboardPage.editHeader')
								: t('createLeaderboardPage.header')}
						</h2>
					</div>
				</div>
				<FormProvider {...pageForm}>
					<Form
						data-testid="leaderboardForm"
						layout="vertical"
						onFinish={() => {
							isTriggeredBySubmit = true;
							pageForm.handleSubmit(onSubmit)();
						}}
					>
						<Row gutter={16}>
							<Col span={16}>
								<Row gutter={24}>
									<Col span={12} data-testid="apiNameGroup">
										<FormInput
											control={pageForm.control}
											disable={!!props?.leaderboard?.apiName}
											translationKey="createLeaderboardV2Page.apiName"
										/>
									</Col>
									<Col span={12} data-testid="titleGroup">
										<FormInput
											control={pageForm.control}
											translationKey="createLeaderboardPage.title"
										/>
									</Col>
								</Row>
							</Col>
						</Row>
						<Row gutter={24} className={styles.formRow}>
							<Col span={24}>
								<Row>{t('createLeaderboardPage.sortOrderV2.label')}</Row>
								<Row className="ant-form-item-explain hint">
									{t('createLeaderboardPage.sortOrderV2.hint')}
								</Row>
							</Col>
						</Row>
						<Row>
							<Col span={24} style={{ marginLeft: 20, marginTop: 4 }}>
								<Controller
									control={pageForm.control}
									name="sortOrder"
									render={({ field: { onChange, value } }) => (
										<Radio.Group onChange={onChange} value={value}>
											<Space direction="vertical">
												<Radio value={LeaderboardsSortOrder.HIGHER_IS_BETTER}>
													{t(
														`createLeaderboardPage.sortOrderV2.sortOrderEnum.higherIsBetter`,
													)}
												</Radio>
												<Radio
													data-testid="leaderboardSortOrderHighest"
													value={LeaderboardsSortOrder.LOWER_IS_BETTER}
												>
													{t(
														`createLeaderboardPage.sortOrderV2.sortOrderEnum.lowerIsBetter`,
													)}
												</Radio>
											</Space>
										</Radio.Group>
									)}
								/>
							</Col>
						</Row>
						<Row gutter={24} className={styles.formRow}>
							<Col span={24}>
								<Row>{t('createLeaderboardPage.scoreType.label')}</Row>
								<Row className="ant-form-item-explain hint">
									{t('createLeaderboardPage.scoreType.hint')}
								</Row>
							</Col>
						</Row>
						<Row>
							<Col span={24} style={{ marginLeft: 20, marginTop: 4 }}>
								<Controller
									control={pageForm.control}
									name="scoreType"
									render={({ field: { onChange, value } }) => (
										<Radio.Group onChange={onChange} value={value}>
											<Space direction="vertical">
												<Radio value={LeaderboardScoreType.POINT}>
													{t(
														`createLeaderboardPage.scoreType.scoreTypeEnum.point`,
													)}
												</Radio>
												<Radio value={LeaderboardScoreType.PERCENTAGE}>
													{t(
														`createLeaderboardPage.scoreType.scoreTypeEnum.percentage`,
													)}
												</Radio>
												<Radio value={LeaderboardScoreType.DISTANCE_IN_FOOT}>
													{t(
														`createLeaderboardPage.scoreType.scoreTypeEnum.distanceInFoot`,
													)}
												</Radio>
												<Radio value={LeaderboardScoreType.DISTANCE_IN_METERS}>
													{t(
														`createLeaderboardPage.scoreType.scoreTypeEnum.distanceInMeters`,
													)}
												</Radio>
												<Radio value={LeaderboardScoreType.TIME_IN_MILLISECOND}>
													{t(
														`createLeaderboardPage.scoreType.scoreTypeEnum.timeInMillisecond`,
													)}
												</Radio>
												<Radio value={LeaderboardScoreType.TIME_IN_SECOND}>
													{t(
														`createLeaderboardPage.scoreType.scoreTypeEnum.timeInSecond`,
													)}
												</Radio>
											</Space>
										</Radio.Group>
									)}
								/>
							</Col>
						</Row>

						<Row gutter={24} className={styles.formRow}>
							<Col span={24}>
								<Row>{t('createLeaderboardPage.entryWritePolicy.label')}</Row>
								<Row className="ant-form-item-explain hint">
									{t('createLeaderboardPage.entryWritePolicy.hint')}
								</Row>
							</Col>
						</Row>
						<Row>
							<Col span={24} style={{ marginLeft: 20, marginTop: 4 }}>
								<Controller
									control={pageForm.control}
									name="entryWritePolicy"
									render={({ field: { onChange, value } }) => (
										<Radio.Group onChange={onChange} value={value}>
											<Space direction="vertical">
												<Radio
													value={
														LeaderboardEntryWritePolicy.CLIENT_AUTHORITATIVE
													}
												>
													{t(
														`createLeaderboardPage.entryWritePolicy.entryWritePolicyEnum.clientAuthoritative`,
													)}
												</Radio>
												<Radio
													value={
														LeaderboardEntryWritePolicy.SERVER_AUTHORITATIVE
													}
												>
													{t(
														`createLeaderboardPage.entryWritePolicy.entryWritePolicyEnum.serverAuthoritative`,
													)}
												</Radio>
											</Space>
										</Radio.Group>
									)}
								/>
							</Col>
						</Row>
						<Row gutter={24} className={styles.formRow}>
							<Col span={24}>
								<Row>{t('createLeaderboardPage.isPublic.label')}</Row>
								<Row>
									<Controller
										control={pageForm.control}
										name="isPublic"
										render={({ field: { onChange, value } }) => (
											<Checkbox checked={value} onChange={onChange}>
												{t('createLeaderboardPage.isPublic.hint')}
											</Checkbox>
										)}
									/>
								</Row>
							</Col>
						</Row>
						<Row gutter={24} className={styles.formRow}>
							<Col span={24}>
								<Row>
									{t('createLeaderboardPage.enableSurpassedNotification.label')}
								</Row>
								<Row>
									<Controller
										control={pageForm.control}
										name="enableSurpassedNotification"
										render={({ field: { onChange, value } }) => (
											<Checkbox
												disabled={!watchIsPublic}
												onChange={onChange}
												checked={value}
											>
												{t(
													'createLeaderboardPage.enableSurpassedNotification.hint',
												)}
											</Checkbox>
										)}
									/>
								</Row>
							</Col>
						</Row>
						<Row gutter={24} style={{ marginTop: 24 }}>
							<Button
								className="ant-btn color-dark"
								data-testid="cancel"
								onClick={navigateToLeaderboardsIndex}
							>
								{t('common.cancel')}
							</Button>
							<Button
								className="ant-btn-primary"
								data-testid="submit"
								style={{ marginLeft: '8px' }}
								htmlType="submit"
								loading={confirmLoading}
							>
								{props?.leaderboard
									? t('common.SaveChanges')
									: t('createLeaderboardPage.create')}
							</Button>
						</Row>
					</Form>
				</FormProvider>
			</div>
		</Spin>
	);
}
